/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import React from 'react';
import PropTypes from 'prop-types';
import { getQuery, getCookie } from 'sutils';
import isEqual from 'lodash/isEqual';
import once from 'lodash/once';
import i18n from '@i18next/i18n';
import { resetDefaultTdwData } from '@tencent/tencent-doc-report';
import { tencentDocOpenUrlReload } from '@tencent/tencent_doc_open_url';
import Modal from '@tencent/dui/lib/components/Modal';
import { Unsubscribe } from 'redux';
// 初始化 God 保证 window.pray 可用
import '@src/page/toc/common/God';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import Aegis from '@src/slide/infrastructure/module/dependent/report/platform/aegis';
import TimeRecord from '@src/slide/infrastructure/module/independent/time-record';
import { TDWActionType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import ArkLoader from '@src/js/common/ark-extensions';
import { initScriptLoader } from '@src/js/common/utils';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import {
    RoomAction,
    PlayerAction,
    MEETING_SCENE_SOURCE,
    LOGIN_ROLE,
    ROLE,
} from '@src/page/toc/common/meeting-room-manager/message_const';
import { getDocId } from '@src/page/toc/common/meeting-room-manager/utils/index';
import store, { registerHooks } from '@player/src/store';
import SlideHelper, { SlideHelperEventsKeys } from '@player/src/sdk/SlideHelper';
import PresenterView, { PresenterViewProps } from '@src/framework/presenter-view';
import { jumpPageRemote } from '@player/src/actions/control';
import actions from '@player/src/actionTypes';
import { setDocViewDirectionInTencentMeeting, initInTencentMeeting } from '@utils/tencent-meeting';

import { PlayerHelper } from './playerEventHandler';
import { loadSlideStore } from './fetchData';
import ua from '@tencent/docs-user-agent';
import { isSpecialActivity } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import MeetingEventBus, { ISlidePlayerState } from '@src/page/toc/common/meeting-room-manager/components/eventBus';
import { PlayerService } from './service/player';
import { MainPlayerService } from './service/main-player';
import { laserBoardEventBus, annoationEventBus, laserBoardEventsKeys, annoationEventsKeys } from './event-bus-instance';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import RainbowManager from '@src/js/common/rainbow';
import AudioControl from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/audio-control';
import { MessageProtocol } from '@src/slide/infrastructure/module/independent/windows-message-protocol';
import { onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { WechatHelper } from '@src/slide/infrastructure/module/independent/wechat-helper';
import { IPPTPlayerAppServices } from '@src/slide/application/entry/toc/ppt-player/ppt-player-app-services.interface';
import { INewMeetingRoomAppServices } from '@src/slide/application/entry/toc/new-meeting-room/new-meeting-room-app-services.interface';
import { AppEntryType } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';
import { IRemoteMediaSyncManager } from '@src/slide/application/service/remote-media-sync-manager/core/interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { ConnectionController } from '@tencent/docs-connection';

Aegis.getInstance().aegis.setConfig({
    // 约定 ext1 标识 <渠道>
    ext1: getCookie('open_theme_id'),
    // 约定 ext2 标识 <场景>
    ext2: getQuery('sceneSource'),
});

// TODO: 重新封装上报
const reportedKeyMap = {};
const reportTime = (key: string, once = false) => {
    if (once && reportedKeyMap[key]) return;

    reportedKeyMap[key] = true;
    Aegis.getInstance().aegis.reportTime(key, performance.now());
};

function preparePlayerInitData() {
    const { clientVars } = window;
    const collabClientVars = clientVars?.collab_client_vars;
    const client = collabClientVars?.client;
    const playerInitData = {
        rev: collabClientVars.rev,
        domainId: clientVars.domainId,
        padId: clientVars.padId,
        globalPadId: clientVars.globalPadId,
        userId: clientVars.userId,
        pver: collabClientVars.pver,
        gray: collabClientVars.gray,
        appid: collabClientVars.apid,
        wmode: collabClientVars.wmode,
        client: {
            upid: client.sid,
            sid: client.upid,
            tid: client.tid,
            sig: client.sig,
        },
    };

    return playerInitData;
}

enum SlidePlayerStatus {
    /** 初始化 */
    initing = 1,
    /** 无权限，跳转空白页 */
    blankpage = 2,
    /** player 数据就绪，等待播放 */
    // docReady = 3,
    /** 开始播放，展示 player */
    playing = 4,
}

type State = Pick<PresenterViewProps, 'data' | 'currentPage' | 'currentFragment' | 'iaFragmentInfo' | 'laserTurnOn' | 'isPresenterView'>
& {
    status: SlidePlayerStatus;
};

interface SlidePlayerProps {
    sceneSource?: MEETING_SCENE_SOURCE;
    inEditor?: boolean;
    isPresenterView?: boolean;
    onfetchPPTSuccess?: (pptData) => void;
    playIndexChange?: (index: number) => void;
}

const getRole = () => {
    const login = getQuery('login');
    const role = getQuery('role');
    const sceneSource = getQuery('sceneSource');
    if (login === LOGIN_ROLE.SPEAKER) return ROLE.MASTER;
    if (login === '1') return ROLE.MASTER;
    if (login === LOGIN_ROLE.AUDIENCE) return ROLE.AUDIENCE;
    if (role === ROLE.MASTER) return ROLE.MASTER;
    if (role === ROLE.AUDIENCE) return ROLE.AUDIENCE;
    // 如果query上都没有符合条件的场景，通过sceneSource来判断默认值，如果是meetingroom，默认值为ROLE.AUDIENCE，其他sceneSource默认值为ROLE.MASTER
    if (sceneSource === MEETING_SCENE_SOURCE.MeetingRoom) return ROLE.AUDIENCE;
    return ROLE.MASTER;
};

const onFirstRender = once(() => {
    // 再延迟 200ms 防止闪动
    setTimeout(() => {
        // 隐藏首屏 loading
        window.firstScreenBrandLoading?.hide();
        // 隐藏 ssr 内容
        const placeholderContainer = document.querySelectorAll('.placeholder-container');
        placeholderContainer.forEach(dom => dom.remove());
    }, 200);
});

/** 上报 ssr 数据 */
const reportSSRTiming = () => {
    const data: { success: boolean, fp: number } | undefined = (window as any).__MEETING_ROOM_SSR__;
    if (!data) return;

    if (data.success) {
        Aegis.getInstance().aegis.reportEvent({ name: 'SSR SUCCESS' });
        Aegis.getInstance().aegis.reportTime('SSR FP', data.fp);
    } else {
        Aegis.getInstance().aegis.reportEvent({ name: 'SSR FAILED' });
    }
};

reportSSRTiming();

export default class SlidePlayer extends React.Component<SlidePlayerProps, State> {
    // 声明Context对象属性
    // TOOD: remove old context api
    static childContextTypes = {
        docID: PropTypes.string,
        roomId: PropTypes.string,
    };

    roomId = getQuery('roomId');
    docId = getDocId();
    // FIXME: should validate
    role = getRole();
    sceneSource: MEETING_SCENE_SOURCE = getQuery('sceneSource') || MEETING_SCENE_SOURCE.None;

    /** 是否显示演讲者视图 */
    // FIXME: 兼容 typo，全量后删除
    isPresenterView = ua.isPC && (getQuery('isPresenterView') === '1' || getQuery('isPresenetView') === '1');
    /** 注册事件的销毁函数集合 */
    eventsOffCallbacks: Array<() => void> = [];

    // TODO: remove
    slideHelper = SlideHelper.getInstance();

    // redux 监听解绑函数
    private subscribeUnbind: Unsubscribe;

    /** 多窗口时与父窗口通信的通道；场景：本地播放的演讲者视图主副窗 */
    private mainPlayerService?: ReturnType<SlidePlayer['initService']>;

    private isBoundSyncVideo = false;
    constructor(props: SlidePlayerProps) {
        super(props);

        this.state = {
            data: {} as any,
            currentPage: parseInt(getQuery('startPage'), 10) || 0,
            // TODO: support init currentFragment
            currentFragment: 0,
            status: SlidePlayerStatus.initing,

            isPresenterView: props.isPresenterView ?? (this.isMaster && this.isPresenterView),
        };

        if (props.sceneSource) {
            this.sceneSource = props.sceneSource;
        }

        // /src/js/service/boot-service/boot-states-pc/pc-first-slide-render-state.ts#L81
        // 独立播放器不走编辑态，需要在播放器内对部分服务初始化
        if (!props.inEditor) {
            onSlideAppReady(() => {
                if (ENV.IS_PC_LOCAL_EDIT) {
                    // ark loader 初始化
                    ArkLoader.init(new ConnectionController({} as any));
                } else {
                    // 备注视图不需要 socket，v3 逻辑不需要特殊处理，沿用 v2
                    initScriptLoader();
                    ArkLoader.meetingRoomPlayerInit(new ConnectionController({} as any));
                }

                // TODO: remove later
                // 告知会议，PPT需要横屏展示
                setDocViewDirectionInTencentMeeting();
                initInTencentMeeting();
            });

            this.initCopyPasteManager();
        }

        window.log?.info(`[RemoteShow]:player roomId=${this.roomId},docId=${this.docId},sceneSource=${this.sceneSource}`);
        this.listenDataError();
        this.getLinkManager().playerInFrame = this.isPlayerInFrame;
        this.registerEventHandler();
        MeetingEventBus.playerSendEnable = this.isPlayerInFrame;
        this.subscribeAction();

        if (ENV.IS_SUBSIDIARY) {
            // 存在部分端的安全策略开启了 noopener, 此时功能不可用, 提示用户切换浏览器
            if (!window.opener) {
                Modal.confirm({
                    content: i18n.t('很抱歉，当前客户端不支持备注视图，请切换浏览器使用此功能。'),
                    onOk: () => window.close(),
                    okText: i18n.t('关闭页面'),
                    cancelText: null,
                    closable: false,
                });
                return;
            }

            this.mainPlayerService = this.initService(window.opener);

            this.mainPlayerService?.getClientVars().then((clientVars) => {
                window.log.info('[Subsidiary] get clientVars success');
                window.clientVars = clientVars;
                dispatchEvent(new Event('dataReady'));
            });

            // 备注窗口关闭时向主窗口通知事件(桌面端close时无法触发beforeunload)
            window.onunload = () => {
                window.opener.postMessage({ type: 'PRESENTER_VIEW_CLOSE' }, '*');
            };
        }

        if (browser.isMobile) {
            // 加载jssdk，提供和微信、qq的交互支持
            WechatHelper.getInstance().install(() => {
                window.wx?.miniProgram?.postMessage?.({
                    data: {
                        title: window.clientVars.title,
                        type: 4,
                    },
                });
            });
        }
    }

    componentDidMount() {
        this.fetchActivityConfig();
        this.fetchPPT();
        this.tdwPageExpose();
    }
    // 运营活动拉取配置
    fetchActivityConfig(): void {
        if (!isSpecialActivity) return;
        RainbowManager.getInstance().getConfigByKey('activity')
            .then((value) => {
                if (typeof value !== 'string') {
                    document.title = window.clientVars.title || '';
                    return;
                }
                const activityConfig = JSON.parse(value);
                document.title = activityConfig.title;
                AudioControl.getInstance().setAudioUrl(activityConfig.audio_url || null);
                AudioControl.getInstance().setConfigStatus(true);
                if (ua.isWeChat && ua.isIOS) {
                    // 微信h5 自动播放音频，仅支持ios;若自动播放不成功，降级为点击后播放音频
                    window.wx?.ready(() => {
                        AudioControl.getInstance().playAudio();
                    });
                }
            })
            .catch((err) => {
                document.title = window.clientVars.title || '';
                AudioControl.getInstance().setConfigStatus(false);
                window.log.error('fail to fetch rainbow config', err);
            });
    }

    /**
     * 事件处理函数
     */
    async registerEventHandler() {
        this.eventsOffCallbacks.push(
            MeetingEventBus.onRecvRoomEvent(RoomAction.ShowPlayerContent, ({ currentPage, currentRev }) => {
                PlayerHelper.getInstance().roomState.currentRev = currentRev;
                PlayerHelper.getInstance().roomState.currentPage = currentPage;

                // 数据过期，重新获取
                if (this.isPptDataExpired) {
                    MeetingEventBus.sendPlayerEvent2Room(PlayerAction.Loading);
                    this.fetchPPT();
                    return;
                }
                window.log?.info('[RemoteShow]:live using cache data');
                this.setState({ currentPage });
                this.showPlayer();
            }),
            MeetingEventBus.onRecvRoomEvent(RoomAction.ToggleView, () => {
                this.setState({ isPresenterView: !this.state.isPresenterView });
            }),
            MeetingEventBus.onRecvRoomEvent(RoomAction.RemoteLaserMove, ({ laserInfo }) => {
                laserBoardEventBus.emit(laserBoardEventsKeys.DRAW_LASER_CUSOR, laserInfo);
            }),
            MeetingEventBus.onRecvRoomEvent(RoomAction.AnnotationMessage, (boardMessage) => {
                annoationEventBus.emit(annoationEventsKeys.CLIENT_BOARD_MESSAGE, boardMessage);
            }),
        );
        const remoteMediaSyncManager = await this.getRemoteSyncMediaManager();
        if (remoteMediaSyncManager) {
            this.eventsOffCallbacks.push(
                remoteMediaSyncManager.attachRecvPlayerEventInRoom(),
                remoteMediaSyncManager.attachLiveManagerMediaEvent(),
            );
        }
    }

    tdwPageExpose() {
        tdw({
            opername: 'doc_ppt',
            module: 'remote_watch',
            action: 'player_exposure',
            ver6: JSON.stringify({
                activity: getQuery('activity'),
            }),
        });
    }

    componentDidUpdate() {
        const { status } = this.state;
        if (status === SlidePlayerStatus.initing || status === SlidePlayerStatus.blankpage) return;
        onFirstRender();
    }

    componentWillUnmount() {
        this.eventsOffCallbacks.forEach(unbind => unbind());
        this.subscribeUnbind();
        onFirstRender();
        this.getRemoteSyncMediaManager().then(ins => ins?.reset());
        // 重置状态，否则从远程演示退出后，正常演示态点击链接也不跳转
        this.getLinkManager().playerInFrame = false;
    }

    getChildContext() {
        return {
            // TODO: 真的有地方用么
            docID: this.docId,
            roomId: this.roomId,
        };
    }

    get isMaster() {
        return this.role === ROLE.MASTER;
    }

    get isPlayerInFrame() {
        return [
            MEETING_SCENE_SOURCE.MeetingRoom,
            MEETING_SCENE_SOURCE.InEditor,
            MEETING_SCENE_SOURCE.RemoteControl,
        ].includes(this.sceneSource);
    }

    /** 校验 pptdata 是否过期 */
    get isPptDataExpired() {
        const masterRev = PlayerHelper.getInstance().roomState.currentRev;
        const myRev = window.pray().revision;
        return masterRev && myRev < masterRev;
    }

    waitForSlideAppReady = () => {
        // 2.0 中 SDK 是在 appService 的 constructor 中初始化的， 3.0 的 SDK 是在 appService init 中初始化
        // 所以在 ui-data-service 中，2.0 bindPresentation 的时候，SDK 已经初始化，上层的 PresentationModel 转换是在 SlidePlayerV2 之前初始化的
        // 3.0 中的各个 service 是走的微任务，所以 bindPresentation 的时候，SDK 还未初始化，上层的 PresentationModel 转换是在 SlidePlayerV2 之后初始化的
        // 本质上来说这里需要的是上层 presentationModel 转换完成，所以这里需要等待上层 presentationModel 转换完成
        if (this.getAppServices().getUiDataService()?.presentation?.converted) {
            return true;
        }
        return new Promise(resolve => eventBus.on(EventKeys.PRESENTATION_CONVERTED, () => resolve(true)));
    };

    // TODO: 有时序问题，需要在 clientvars.js 处修正
    listenDataError = () => {
        // 权限错误，立即跳转空白页
        // 其他错误不处理，直到重试达到上限

        window.addEventListener('dataError', (e) => {
            const retcode = (e as any)?.detail?.retcode;

            Aegis.getInstance().aegis.reportEvent({
                name: 'ClientVars Failed',
                ext3: retcode,
            });
        });

        // 重试达到上限
        window.addEventListener('dataError-RetryLimitReached', () => {
            Aegis.getInstance().aegis.reportEvent({ name: 'ClientVars RetryLimitReached' });
            Modal.confirm({
                content: i18n.t('加载文档数据出错'),
                onOk: tencentDocOpenUrlReload,
                okText: i18n.t('立即刷新'),
                cancelText: null,
                closable: false,
            });

            // TODO: remove later
            // sdk 化全量后，房间层也是弹窗
            MeetingEventBus.sendPlayerEvent2Room(PlayerAction.Error, { lastError: i18n.t('加载文档数据出错') });
        });
    };

    async fetchPPT() {
        reportTime('waitForSlideAppReadyStart', true);
        Aegis.getInstance().aegis.time('waitForSlideAppReady');
        await this.waitForSlideAppReady();
        Aegis.getInstance().aegis.timeEnd('waitForSlideAppReady');

        Aegis.getInstance().aegis.time('loadSlideStore');
        const data = loadSlideStore();
        Aegis.getInstance().aegis.timeEnd('loadSlideStore');

        window.log?.info('[RemoteShow]:checkData OK');
        resetDefaultTdwData();
        this.onfetchPPTSuccess(data);
    }

    /** fetchPPT 成功回调，**只在 this.fetchPPT** 中使用; 数据获取相关逻辑较乱，考虑到可读性独立出来 */
    async onfetchPPTSuccess(pptData) {
        this.setState({ data: pptData });
        this.props.onfetchPPTSuccess?.(pptData);

        reportTime('playerReady', true);
        this.updateDocumentTitle();

        // mode1 - 多窗口
        if (ENV.IS_SUBSIDIARY) {
            this.mainPlayerService?.getPlayerStore().then((store) => {
                const { currentPage, currentFragment, laserTurnOn, iaFragmentInfo } = store as any;
                this.setState({ currentPage, currentFragment, laserTurnOn, iaFragmentInfo });
                this.showPlayer();
            });
        }

        // mode2 - 父子frame
        MeetingEventBus.sendPlayerEvent2Room(PlayerAction.PlayerReady, { playerInitData: preparePlayerInitData() });
        this.sendPlayerStateToRoom();

        // mode - 3 独立
        if (this.sceneSource === MEETING_SCENE_SOURCE.TOBPlayer) {
            // ToB 没有房间层，无需等待直接 showPlayer
            this.showPlayer();
            this.slideHelper.dispatchEvent(SlideHelperEventsKeys.ON_LOAD);
        }

        // 常规场景，等待房间层下发通知 (onRoomStateChange) 再 showPlayer
        if (!this.isBoundSyncVideo) {
            const remoteMediaSyncManager = await this.getRemoteSyncMediaManager();
            if (remoteMediaSyncManager) {
                const detachedFn = remoteMediaSyncManager.attachRecvMediaChangeInPlayer();
                this.eventsOffCallbacks.push(detachedFn);
            }
            this.isBoundSyncVideo = true;
        }
    }

    updateDocumentTitle() {
        if (isSpecialActivity) return;
        const { title } = window.clientVars;
        // 默认title设置为文档名
        let documentTitle = title;
        // 备注视图标题重置
        if (ENV.IS_SUBSIDIARY) {
            documentTitle = `${i18n.t('备注视图')}-${title}`;
        }
        document.title = documentTitle;
    }

    // TODO:
    subscribeAction() {
        this.subscribeUnbind = store.subscribe(() => {
            const playState = store.getState();
            const { lastAction } = playState;
            window.log?.info(`[RemoteShow]:player state change: ${lastAction.type}`);

            const gotoPageFragmentArr = [
                actions.NEXT_PAGE,
                actions.PREV_PAGE,
                actions.PREV_PAGE_WITHOUT_FRAGMENT,
                actions.NEXT_PAGE_WITHOUT_FRAGMENT,
                actions.INTERACTIVE_ANIM_NEXT,
                actions.JUMP_PAGE,
                actions.STOP_MAIN_ANIM,
                actions.INTERACTIVE_ANIM_RESET,
            ];
            const playerStateArr = [
                actions.TURN_ON_LASER,
                actions.TURN_OFF_LASER,
                actions.INIT_CLIENT_WIDTHHEIGHT,
                actions.SLIDE_SCALE_CHANGED,
                actions.NEXT_PAGE_REMOTE,
                actions.PREV_PAGE_REMOTE,
                actions.JUMP_PAGE_REMOTE,
            ];

            if (gotoPageFragmentArr.includes(lastAction.type)) {
                tdw({
                    opername: 'doc_ppt',
                    module: 'remote_watch',
                    action: 'page_turn',
                });
                this.sendGotoPageFragmentToRoom();
                return;
            }

            if (playerStateArr.includes(lastAction.type)) {
                this.sendPlayerStateToRoom();
                return;
            }

            if (lastAction.type === actions.SYCN_WITH_MASTER) {
                tdw({
                    opername: 'doc_ppt',
                    module: 'remote_watch',
                    action: 'schedule',
                });
                this.syncWithMasterPage();
                return;
            }

            if (lastAction.type === actions.TURN_ANNOTATION) {
                MeetingEventBus.sendPlayerEvent2Room(PlayerAction.TurnAnnotation, lastAction.payload);
                return;
            }
        });
    }

    /** 发送 GotoPageFragment 到房间层 */
    sendGotoPageFragmentToRoom(): void {
        const playerState = this.getPlayerState();
        if (playerState) {
            MeetingEventBus.sendPlayerEvent2Room(PlayerAction.GotoPageFragment, playerState);
        }
    }

    /** 发送 PlayerState 到房间层 */
    sendPlayerStateToRoom(): void {
        const playerState = this.getPlayerState();
        if (playerState) {
            MeetingEventBus.sendPlayerEvent2Room(PlayerAction.PlayerState, playerState);
        }
    }

    getPlayerState(): ISlidePlayerState | undefined {
        const control = store.getState()?.control as any;
        if (!control) return;

        const { currentPage, currentFragment, laserTurnOn, isManulFlipped, scale, iaFragmentInfo,
            shouldStopMainAnim, enableAnnotation, isLoadPageBySwitchForward, shouldStopTransition } = control;

        return {
            currentPage: currentPage || 0,
            currentFragment: currentFragment || 0,
            pageCount: this.state.data.presentation?.slideCount || 1,
            laserTurnOn: !!laserTurnOn,
            enableAnnotation: !!enableAnnotation,
            isManulFlipped: !!isManulFlipped,
            scale: scale || 1.0,
            iaFragmentInfo: iaFragmentInfo || {},
            shouldStopMainAnim,
            isLoadPageBySwitchForward,
            shouldStopTransition,
        };
    }

    showPlayer = () => {
        if (this.state.status === SlidePlayerStatus.playing) return;

        reportTime('showPlayer', true);

        TimeRecord.start(TDWActionType.PLAYER_ENTER, { from: -3 });

        this.setState({ status: SlidePlayerStatus.playing });
        this.sendPlayerStateToRoom();

        if (this.isPlayerInFrame) {
            this.syncWithMasterPage();
        }
    };

    /** 重置 player 页面位置，恢复为房间层状态 */
    syncWithMasterPage() {
        if (getQuery('login') === '1') return;

        function getPlayerCurrentPage() {
            const ctrl = store.getState().control as any;
            // FIXME: ctrl.isManulFlipped 永远是 false； 若使用 remoteCurrent 的数据逻辑上也不太理解;
            const useRemote = ctrl.remotePlayRole === actions.AUDIENCE && ctrl.allowFilpPage && ctrl.isManulFlipped;

            return useRemote ? {
                p: ctrl.remoteCurrentPage,
                f: ctrl.remoteCurrentFragment,
                iaF: ctrl.remoteIaFragmentInfo,
                stopA: ctrl.shouldStopMainAnim,
            } : {
                p: ctrl.currentPage,
                f: ctrl.currentFragment,
                iaf: ctrl.iaFragmentInfo,
                stopA: ctrl.shouldStopMainAnim,
            };
        }

        const playerPage = getPlayerCurrentPage();
        const { roomState } = PlayerHelper.getInstance();
        const roomPage = {
            p: roomState.currentPage || 0,
            f: roomState.currentFragment || 0,
            iaF: roomState.iaFragmentInfo || 0,
            stopA: roomState.shouldStopMainAnim,
        };

        if (roomPage.p !== playerPage.p || roomPage.f !== playerPage.f || !isEqual(roomPage.iaF, playerPage.iaF)) {
            store.dispatch(jumpPageRemote(roomPage));
        }
    }

    handleEndPlay = (pageIndex: number) => {
        // FIXME:
        if (getCookie('open_theme_id') === 'tencent_meeting') {
            window.parent?.dispatchEvent(new Event('live-components:tencent-meeting-end-play'));
            return;
        }

        MeetingEventBus.sendPlayerEvent2Room(PlayerAction.EndPlay, { pageIndex });
        eventBus.emit(EventKeys.SLIDE_PLAYER_END_PLAY);
    };

    // FIXME:
    handlePlayerLoaded = () => {
        // if (!SlidePlayer.isMaster()) {
        //     store.dispatch(beAudience());
        // }
        // this.onAllowFlipChanged(this.liveManager.liveInfo.allow_flip);
        // this.onAllowFlipChanged(true);
    };

    playIndexChange = (index: number): void => {
        const selectionService = this.getSelectionService();
        if (selectionService) {
            selectionService.currentSlideIndex = index;
        }
        this.props.playIndexChange?.(index);
    };

    /** TODO: 1. rename 2. 销毁监听 */
    initService(targetWindow: Window) {
        // 注册己方服务
        MessageProtocol.getInstance().registerService(PlayerService);

        // 创建 targetWindow 服务代理
        const serviceProxy = MessageProtocol.getInstance().createServiceProxy(targetWindow, MainPlayerService);
        // hook redux, 同步 action 到 targetWindow
        registerHooks({ beforeAction: action => serviceProxy.dispatchAction(action) });
        // 同步激光笔事件到 targetWindow
        laserBoardEventBus.on(laserBoardEventsKeys.CUSOR_MOVE, position => serviceProxy.syncMousemove(position));
        this.getNoteViewMediaSyncManager()?.promise()
            .then((ins) => {
                ins.initServiceProxy(serviceProxy);
            });
        // 同步备注修改
        window.addEventListener(
            'SUBSIDIARY_PLAYER_UPDATE_NOTE',
            (e: CustomEvent<{ slideId: string, paragraphList: any, textBodyStyle: any }>) => {
                const { slideId, paragraphList, textBodyStyle } = e.detail;
                serviceProxy.syncUpdateNote(slideId, paragraphList, textBodyStyle);
            },
        );

        return serviceProxy;
    }

    render() {
        const { status } = this.state;

        if (status === SlidePlayerStatus.initing) return null;

        return (
            <PresenterView
                style={{
                    visibility: status === SlidePlayerStatus.playing ? 'visible' : 'hidden',
                }}
                docId={this.docId}
                handPlayerLoaded={this.handlePlayerLoaded}
                handleEndPlay={this.handleEndPlay}
                sceneSource={this.sceneSource}
                isRoleMaster={this.isMaster}
                playIndexChange={this.playIndexChange}
                {...this.state}
                services={{
                    mediaManager: this.getMediaManager(),
                    playManager: this.getPlayManager(),
                    liveManager: this.getLiveManager(),
                    watermarkManager: this.getAppServices().viewableServiceLayer.watermarkManager,
                }}
            />
        );
    }

    // 这个文件在 pc 端编辑入口、new-meeting-room、以及 pptplayer(目前看备注视图用的是这个入口)都存在，所以这里 appservice 可能有三种
    private getAppServices = () => AppServiceProvider.getInstance().getAppServices() as
        (IPPTPlayerAppServices | INewMeetingRoomAppServices | IPcEditorAppServices);

    private getSelectionService = (): ISelectionService | undefined => this.getAppServices()
        .viewableServiceLayer?.selectionService;

    private getMediaManager = () => this.getAppServices().viewableServiceLayer.mediaManager;

    private getPlayManager = () => this.getAppServices().viewableServiceLayer.playManager;

    private getLinkManager = () => this.getAppServices().viewableServiceLayer.linkManager;

    private getLiveManager = () => this.getAppServices().viewableServiceLayer.liveManager;

    // 备注视图视频同步manager的入口为 pc 端编辑入口 和 ppt-player
    private getNoteViewMediaSyncManager = () => {
        if (![AppEntryType.PC_EDIT, AppEntryType.PPT_PLAYER]
            .includes(this.getAppServices().getEntryType())) return undefined;
        return (this.getAppServices() as (IPPTPlayerAppServices | IPcEditorAppServices)).viewableServiceLayer
            .noteViewMediaSyncManager;
    };

    // 远程演示视频同步manager的入口为 pc 端编辑入口 和 new-meeting-room
    private getRemoteSyncMediaManager = async (): Promise<IRemoteMediaSyncManager | undefined> => {
        if (![AppEntryType.PC_EDIT, AppEntryType.MEETING_ROOM]
            .includes(this.getAppServices().getEntryType())) return undefined;
        const remoteMediaSyncManager = await (this.getAppServices() as (INewMeetingRoomAppServices
            | IPcEditorAppServices)).viewableServiceLayer.remoteMediaSyncManager.promise();
        remoteMediaSyncManager.init(this.sceneSource);
        return remoteMediaSyncManager;
    };

    private initCopyPasteManager() {
        // copyPasteManager 是异步模块，访问触发初始化
        void this.getAppServices().editableServiceLayer?.copyPasteManager?.promise();
    }
}
